import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Save, User as UserIcon, ShieldCheck, Mail, Loader2 } from "lucide-react";
import { authApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";

export default function UsersManager() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [notifying, setNotifying] = useState(false);

  const load = () => authApi.listUsers().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleNotify = async () => {
    setNotifying(true);
    try {
      const res = await authApi.notifyLoginUrl();
      toast({ title: "Login link emailed", description: `Sent to ${res.sent} of ${res.total} admin user(s).` });
    } catch (e) {
      toast({ title: "Couldn't send", description: e?.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally { setNotifying(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authApi.createUser(form);
      toast({ title: "User created" });
      setOpen(false); setForm({ username: "", password: "", name: "", role: "admin" }); load();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await authApi.deleteUser(deleteId); toast({ title: "User deleted" }); load(); }
    catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Admin Users</h1>
          <p className="text-sm text-slate-500">{items.length} user(s) with access to this panel</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleNotify} disabled={notifying} variant="outline" data-testid="users-notify-login-url" className="rounded-full">
            {notifying ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Mail size={15} className="mr-1.5" />} Email login link
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="users-add-btn" className="bg-orange hover:bg-orange-dark text-white rounded-full"><Plus size={16} className="mr-1.5" /> Add User</Button>
        </div>
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-xl bg-sky-50 border border-sky-100 p-4 text-sm text-slate-600" data-testid="users-login-url-note">
        <Mail size={16} className="text-sky-500 shrink-0 mt-0.5" />
        <span>Admins now sign in at <strong className="text-navy">cassonline.co.uk/login</strong>. New users are emailed this link automatically. Use <strong>“Email login link”</strong> to send it to everyone once.</span>
      </div>

      <div className="space-y-3">
        {items.map((u) => (
          <div key={u.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center text-white"><UserIcon size={18} /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-navy truncate">{u.name || u.username}</p>
              <p className="text-sm text-slate-400 truncate">{u.username}</p>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1"><ShieldCheck size={12} /> {u.role}</span>
            {u.id !== currentUser?.id && (
              <button onClick={() => setDeleteId(u.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500"><Trash2 size={15} /></button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Add Admin User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-navy font-semibold">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" placeholder="Jane Smith" /></div>
            <div><Label className="text-navy font-semibold">Username / Email</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-2" placeholder="jane@cass-online.co.uk" /></div>
            <div><Label className="text-navy font-semibold">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2" placeholder="Strong password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full"><X size={15} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Save size={15} className="mr-1" /> {saving ? "Saving..." : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this user?</AlertDialogTitle><AlertDialogDescription>They will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
